/**
 * Utility functions for detecting and handling Auth0 authentication errors
 */

/**
 * Check if an error is an invalid refresh token error from Auth0
 *
 * Auth0 returns various error formats when a refresh token is invalid:
 * - error.error === 'invalid_grant'
 * - error.message contains 'invalid refresh token'
 * - error.error_description contains 'Unknown or invalid refresh token'
 *
 * @param error - The error object to check (can be any type)
 * @returns true if this is an invalid refresh token error
 */
export const isInvalidRefreshTokenError = (error: unknown): boolean => {
  if (!error) return false;

  // Convert error to string for pattern matching
  const errorString = JSON.stringify(error).toLowerCase();
  const errorMessage = (error as any)?.message?.toLowerCase() || '';
  const errorError = (error as any)?.error?.toLowerCase() || '';
  const errorDescription = (error as any)?.error_description?.toLowerCase() || '';

  // Check all possible formats where Auth0 reports invalid refresh tokens
  return (
    errorError === 'invalid_grant' ||
    errorString.includes('invalid_grant') ||
    errorString.includes('invalid refresh token') ||
    errorString.includes('unknown or invalid refresh token') ||
    errorMessage.includes('invalid_grant') ||
    errorMessage.includes('invalid refresh token') ||
    errorDescription.includes('invalid refresh token')
  );
};

/**
 * Clear all Auth0 cache from localStorage
 * This removes any stale tokens that might prevent fresh authentication
 */
export const clearAuth0Cache = (): void => {
  Object.keys(localStorage).forEach(key => {
    if (key.startsWith('@@auth0spajs@@')) {
      localStorage.removeItem(key);
    }
  });
};

/**
 * Handle invalid refresh token by clearing Auth0 cache and forcing logout
 * This ensures a clean state for re-authentication
 *
 * @param logout - Auth0 logout function from useAuth0 hook
 */
export const clearAuth0CacheAndLogout = (
  logout: (options?: { logoutParams?: { returnTo?: string } }) => void,
): void => {
  console.error('Invalid refresh token detected, clearing cache and logging out');

  // Clear all Auth0 cache from localStorage
  clearAuth0Cache();

  // Force logout and redirect to login
  logout({ logoutParams: { returnTo: window.location.origin } });
};
